import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User } from '../services/authService';
import { goalService } from '../services/goalService';
import { habitService, Habit } from '../services/habitService';
import { scheduleService, ScheduleEvent, CreateScheduleEventData } from '../services/scheduleService';
import PageHeader from './common/PageHeader';
// import TimeblockDetail from './TimeblockDetail'; // TODO: Create TimeblockDetail component
import './Schedule.css';

interface ScheduleProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

// Using ScheduleEvent from the service instead of local Event interface

const Schedule: React.FC<ScheduleProps> = ({ user, onClose, onToggleSidebar }) => {
  const navigate = useNavigate();
  const { date } = useParams<{ date?: string }>();

  // Helper to format date as YYYY-MM-DD using local time
  const formatLocalYYYYMMDD = (d: Date): string => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Validate date string format (YYYY-MM-DD)
  const validateDate = (dateString: string | undefined): string | null => {
    if (!dateString) return null;
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dateString)) return null;
    
    const [year, month, day] = dateString.split('-').map(Number);
    const d = new Date(year, month - 1, day);

    if (d && d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day) {
      return dateString;
    }
    return null;
  };

  // Initialize currentDate from URL param or default to today
  const getInitialDate = (): Date => {
    const validatedDate = validateDate(date);
    if (validatedDate) {
      const [year, month, day] = validatedDate.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    return new Date();
  };

  const [currentDate, setCurrentDate] = useState(getInitialDate());
  const [events, setEvents] = useState<ScheduleEvent[]>([]);
  const [showEventForm, setShowEventForm] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [editingEvent, setEditingEvent] = useState<ScheduleEvent | null>(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>('');
  const [goals, setGoals] = useState<any[]>([]);
  const [habits, setHabits] = useState<Habit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTimeblockDetail, setShowTimeblockDetail] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const timeSlotsRef = useRef<HTMLDivElement>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<ScheduleEvent | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Sync URL parameter with currentDate state - prevent infinite loops
  useEffect(() => {
    const validatedUrlDate = validateDate(date);
    if (validatedUrlDate) {
      // Parse validated date as local date to avoid timezone issues
      const [year, month, day] = validatedUrlDate.split('-').map(Number);
      const urlDate = new Date(year, month - 1, day);
      const currentDateString = formatLocalYYYYMMDD(currentDate);
      
      // Only update if dates differ
      if (validatedUrlDate !== currentDateString) {
        setCurrentDate(urlDate);
      }
    } else {
      // URL has invalid or missing date, redirect to today's date
      const today = formatLocalYYYYMMDD(new Date());
      navigate(`/schedule/${today}`, { replace: true });
    }
  }, [date, navigate]); // Only depend on date param, not currentDate to avoid loops

  // Function to scroll to current time
  const scrollToCurrentTime = useCallback(() => {
    if (!timeSlotsRef.current) return;
    
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    
    // Calculate current time in HH:MM format
    const currentTimeString = `${currentHour.toString().padStart(2, '0')}:${currentMinute.toString().padStart(2, '0')}`;
    
    // Find the closest time slot
    const timeSlots = generateTimeSlots();
    let targetTimeSlot = timeSlots[0]; // Default to first slot
    
    // Find the closest time slot to current time
    for (let i = 0; i < timeSlots.length; i++) {
      if (timeSlots[i] >= currentTimeString) {
        targetTimeSlot = timeSlots[i];
        break;
      }
    }
    
    // If current time is after the last slot, use the last slot
    if (currentTimeString > timeSlots[timeSlots.length - 1]) {
      targetTimeSlot = timeSlots[timeSlots.length - 1];
    }
    
    // Find the time slot element and scroll to it
    const timeSlotElements = timeSlotsRef.current.querySelectorAll('.time-slot');
    timeSlotElements.forEach((element: Element) => {
      const timeLabel = element.querySelector('.time-label');
      if (timeLabel && timeLabel.textContent === targetTimeSlot) {
        (element as HTMLElement).scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }
    });
  }, []);

  // Load events for the current date
  useEffect(() => {
    const loadEvents = async () => {
      if (user) {
        try {
          setLoading(true);
          setError(null);
          const userId = typeof user.id === 'string' ? parseInt(user.id) : user.id;
          const dateString = formatLocalYYYYMMDD(currentDate);
          console.log('Loading events for date:', dateString, 'userId:', userId);
          const eventsForDate = await scheduleService.getScheduleEventsByDate(userId, dateString);
          console.log('Loaded events:', eventsForDate.length, 'events', eventsForDate);
          setEvents(eventsForDate);
        } catch (error) {
          console.error('Error loading events:', error);
          setError('Failed to load events');
          setEvents([]);
        } finally {
          setLoading(false);
        }
      }
    };

    loadEvents();
  }, [user, currentDate]);

  // Scroll to current time when viewing today's date
  useEffect(() => {
    const today = new Date();
    const isToday = currentDate.toDateString() === today.toDateString();
    
    if (isToday && !loading && events.length >= 0) {
      // Small delay to ensure DOM is rendered
      setTimeout(() => {
        scrollToCurrentTime();
      }, 100);
    }
  }, [currentDate, loading, events, scrollToCurrentTime]);

  // Update current time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  // Load goals and habits
  useEffect(() => {
    const loadGoalsAndHabits = async () => {
      if (user) {
        try {
          // Load goals
          const goalsResponse = await goalService.getUserGoals({
            user_id: user.id.toString(),
            limit: 50
          });
          if (goalsResponse.success && goalsResponse.goals) {
            setGoals(goalsResponse.goals);
          }

          // Load habits
          const habitsResponse = await habitService.getUserHabits(typeof user.id === 'string' ? parseInt(user.id) : user.id, {
            is_active: true,
            limit: 50
          });
          if (habitsResponse.success && habitsResponse.habits) {
            setHabits(habitsResponse.habits);
          }
        } catch (error) {
          console.error('Error loading goals and habits:', error);
        }
      }
    };

    loadGoalsAndHabits();
  }, [user]);

  // Handle click outside to close menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  // Refresh events for current date
  const refreshEvents = async () => {
    if (user) {
      try {
        setLoading(true);
        const userId = typeof user.id === 'string' ? parseInt(user.id) : user.id;
        const dateString = formatLocalYYYYMMDD(currentDate);
        const eventsForDate = await scheduleService.getScheduleEventsByDate(userId, dateString);
        setEvents(eventsForDate);
      } catch (error) {
        console.error('Error refreshing events:', error);
        setError('Failed to refresh events');
      } finally {
        setLoading(false);
      }
    }
  };

  const addEvent = async (eventData: CreateScheduleEventData) => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);
      await scheduleService.createScheduleEvent(eventData);
      await refreshEvents();
    } catch (error) {
      console.error('Error creating event:', error);
      setError('Failed to create event');
    } finally {
      setLoading(false);
    }
  };

  const updateEvent = async (eventId: number, updateData: any) => {
    try {
      setLoading(true);
      setError(null);
      await scheduleService.updateScheduleEvent(eventId, updateData);
      await refreshEvents();
    } catch (error) {
      console.error('Error updating event:', error);
      setError('Failed to update event');
    } finally {
      setLoading(false);
    }
  };

  const deleteEvent = async (eventId: number, event?: ScheduleEvent, deleteInstanceOnly: boolean = false) => {
    try {
      setLoading(true);
      setError(null);
      
      if (deleteInstanceOnly && event && event.is_repeating) {
        // Delete only this instance
        const dateString = formatLocalYYYYMMDD(currentDate);
        await scheduleService.deleteScheduleEventInstance(eventId, dateString);
      } else {
        // Delete entire event/series
        await scheduleService.deleteScheduleEvent(eventId);
      }
      
      await refreshEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
      setError('Failed to delete event');
    } finally {
      setLoading(false);
    }
  };

  // Removed getDaysInMonth as it's not currently used in daily view

  const getEventsForDate = (date: Date) => {
    const dateString = formatLocalYYYYMMDD(date);
    console.log('Getting events for date:', dateString, 'from', events.length, 'total events');
    
    const filteredEvents = events.filter(event => {
      if (!event.is_repeating && event.event_date === dateString) {
        console.log('Including one-time event:', event.title, 'for', event.event_date);
        return true;
      }
      if (event.is_repeating) {
        const shouldInclude = scheduleService.shouldEventOccurOnDate(event, dateString);
        console.log('Repeating event:', event.title, 'should occur on', dateString, ':', shouldInclude);
        return shouldInclude;
      }
      return false;
    });
    
    console.log('Filtered events for', dateString, ':', filteredEvents.length, 'events');
    return filteredEvents;
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const navigateDay = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setDate(newDate.getDate() - 1);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    const dateString = formatLocalYYYYMMDD(newDate);
    navigate(`/schedule/${dateString}`);
  };

  const goToToday = () => {
    const today = formatLocalYYYYMMDD(new Date());
    navigate(`/schedule/${today}`);
    setIsMenuOpen(false);
  };

  const goToCurrentHour = () => {
    scrollToCurrentTime();
    setIsMenuOpen(false);
  };

  const handleAddNewEvent = () => {
    setSelectedDate(formatLocalYYYYMMDD(currentDate));
    setSelectedTimeSlot('09:00');
    setEditingEvent(null);
    setShowEventForm(true);
    setIsMenuOpen(false);
  };

  const handleTimeSlotClick = (timeSlot: string) => {
    const dateString = formatLocalYYYYMMDD(currentDate);
    setSelectedDate(dateString);
    setSelectedTimeSlot(timeSlot);
    setEditingEvent(null);
    setShowEventForm(true);
  };

  const handleEventClick = (event: ScheduleEvent) => {
    setEditingEvent(event);
    setSelectedDate(event.event_date || formatLocalYYYYMMDD(currentDate));
    setShowEventForm(true);
  };

  const handleEventDetails = (eventId: number) => {
    setSelectedEventId(eventId);
    setShowTimeblockDetail(true);
  };

  // Removed monthNames and weekDays as they're not currently used in daily view

  // Generate time slots for the day (6 AM to 11 PM in 30-minute intervals)
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 6; hour <= 23; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push(timeString);
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  if (!user) {
    return (
      <div className="schedule-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="schedule-page-title">/schedule</h1>}
        />
        <div className="auth-required">
          <p>Please sign in to access your schedule.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="schedule-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={
          <div className="date-navigation">
            <button className="schedule-nav-btn" onClick={() => navigateDay('prev')}>
              ←
            </button>
            <h2 className="current-date">{formatDate(currentDate)}</h2>
            <button className="schedule-nav-btn" onClick={() => navigateDay('next')}>
              →
            </button>
          </div>
        }
        rightActions={
          <div className="schedule-menu-container" ref={menuRef}>
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)} 
              className="schedule-menu-btn"
              aria-label="More options"
            >
              ⋮
            </button>
            {isMenuOpen && (
              <div className="schedule-dropdown-menu">
                <button 
                  onClick={handleAddNewEvent} 
                  className="schedule-menu-item"
                >
                  Add New Event
                </button>
                <div className="schedule-menu-separator"></div>
                <button 
                  onClick={goToToday} 
                  className="schedule-menu-item"
                >
                  Go to Today
                </button>
                <button 
                  onClick={goToCurrentHour} 
                  className="schedule-menu-item"
                >
                  Go to Current Hour
                </button>
              </div>
            )}
          </div>
        }
      />

      <div className={`schedule-main ${showTimeblockDetail ? 'with-drawer' : ''}`}>

        <div className="schedule-content">
          {error && (
            <div className="error-message">
              <p>⚠️ {error}</p>
              <button onClick={refreshEvents} className="retry-btn">Retry</button>
            </div>
          )}

          <div className="calendar-container">
            <div className="daily-schedule">
              {loading && (
                <div className="loading-overlay">
                  <div className="loading-spinner"></div>
                  <div className="schedule-loading-message">Loading...</div>
                </div>
              )}
              <div className="time-slots" ref={timeSlotsRef}>
                {timeSlots.map((timeSlot, index) => {
                  const eventsAtTime = getEventsForDate(currentDate).filter(event => {
                    // Handle both HH:MM and HH:MM:SS formats from API
                    const eventTime = event.event_time.substring(0, 5); // Extract HH:MM part
                    return eventTime === timeSlot;
                  });
                  const hasEvents = eventsAtTime.length > 0;
                  
                  // Check if this is the current time slot
                  const currentHour = currentTime.getHours();
                  const currentMinute = currentTime.getMinutes();
                  const currentTimeString = `${currentHour.toString().padStart(2, '0')}:${currentMinute.toString().padStart(2, '0')}`;
                  
                  // Find the closest time slot to current time
                  let isCurrentTime = false;
                  const timeSlotHour = parseInt(timeSlot.split(':')[0]);
                  const timeSlotMinute = parseInt(timeSlot.split(':')[1]);
                  
                  // Check if current time falls within this 30-minute slot
                  const currentTimeInMinutes = currentHour * 60 + currentMinute;
                  const slotStartInMinutes = timeSlotHour * 60 + timeSlotMinute;
                  const slotEndInMinutes = slotStartInMinutes + 30;
                  
                  if (currentTimeInMinutes >= slotStartInMinutes && currentTimeInMinutes < slotEndInMinutes) {
                    isCurrentTime = true;
                  }
                  
                  return (
                    <div
                      key={timeSlot}
                      className={`time-slot ${hasEvents ? 'has-events' : 'empty'} ${isCurrentTime ? 'current-time' : ''}`}
                      onClick={() => handleTimeSlotClick(timeSlot)}
                    >
                      <div className="time-label">{timeSlot}</div>
                      <div className="time-content">
                        {eventsAtTime.map(event => {
                          // Calculate how many time slots this event should span
                          const timeSlotMinutes = 30; // Each time slot represents 30 minutes
                          const slotsToSpan = Math.ceil(event.duration_minutes / timeSlotMinutes);
                          const eventHeight = slotsToSpan * 50; // Each slot is 60px tall
                          
                          return (
                            <div
                              key={event.id}
                              className="schedule-event"
                              style={{ 
                                backgroundColor: event.color,
                                height: `${eventHeight}px`,
                                minHeight: `${eventHeight}px`,
                                position: 'absolute',
                                left: '0.5rem',
                                right: '0.5rem',
                                zIndex: 10
                              }}
                            >
                              <div className="event-content" onClick={(e) => {
                                e.stopPropagation();
                                handleEventClick(event);
                              }}>
                                <div className="event-title">{event.title}</div>
                                <div className="event-duration">{event.duration_minutes} min</div>
                                {event.is_repeating && (
                                  <div className="event-link">
                                    🔄 {event.repeat_type}
                                  </div>
                                )}
                              </div>
                              <div className="event-actions">
                                <button
                                  className="event-detail-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEventDetails(event.id);
                                  }}
                                  title="View details and linked items"
                                >
                                  📋
                                </button>
                              </div>
                            </div>
                          );
                        })}
                        {!hasEvents && (
                          <div className="empty-slot">
                            {events.length === 0 ? 'No events today - Click to add first event' : 'Click to add event'}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {showEventForm && (
            <EventForm
              event={editingEvent}
              selectedDate={selectedDate}
              selectedTimeSlot={selectedTimeSlot}
              goals={goals}
              habits={habits}
              user={user}
              onSave={async (eventData) => {
                try {
                  if (editingEvent) {
                    await updateEvent(editingEvent.id, eventData);
                  } else {
                    await addEvent(eventData);
                  }
                  setShowEventForm(false);
                  setEditingEvent(null);
                } catch (error) {
                  console.error('Error saving event:', error);
                }
              }}
              onDelete={editingEvent ? async () => {
                setEventToDelete(editingEvent);
                setShowDeleteModal(true);
              } : undefined}
              onCancel={() => {
                setShowEventForm(false);
                setEditingEvent(null);
              }}
            />
          )}
        </div>

        {/* TODO: Implement TimeblockDetail component */}
        {showTimeblockDetail && selectedEventId && (
          <div className="drawer-overlay" onClick={() => {
            setShowTimeblockDetail(false);
            setSelectedEventId(null);
          }}>
            <div className="right-drawer" onClick={(e) => e.stopPropagation()}>
              <div className="timeblock-detail-placeholder">
                <h3>Event Details</h3>
                <p>Event ID: {selectedEventId}</p>
                <p>TimeblockDetail component not yet implemented</p>
                <button onClick={() => {
                  setShowTimeblockDetail(false);
                  setSelectedEventId(null);
                }}>Close</button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && eventToDelete && (
          <DeleteEventModal
            event={eventToDelete}
            currentDate={currentDate}
            onDeleteInstance={async () => {
              try {
                await deleteEvent(eventToDelete.id, eventToDelete, true);
                setShowDeleteModal(false);
                setEventToDelete(null);
                setShowEventForm(false);
                setEditingEvent(null);
              } catch (error) {
                console.error('Error deleting event instance:', error);
              }
            }}
            onDeleteSeries={async () => {
              try {
                await deleteEvent(eventToDelete.id, eventToDelete, false);
                setShowDeleteModal(false);
                setEventToDelete(null);
                setShowEventForm(false);
                setEditingEvent(null);
              } catch (error) {
                console.error('Error deleting event series:', error);
              }
            }}
            onCancel={() => {
              setShowDeleteModal(false);
              setEventToDelete(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

interface EventFormProps {
  event: ScheduleEvent | null;
  selectedDate: string;
  selectedTimeSlot: string;
  goals: any[];
  habits: Habit[];
  user: User | null;
  onSave: (eventData: CreateScheduleEventData) => Promise<void>;
  onDelete?: () => Promise<void>;
  onCancel: () => void;
}

const EventForm: React.FC<EventFormProps> = ({ event, selectedDate, selectedTimeSlot, goals, habits, user, onSave, onDelete, onCancel }) => {
  const [formData, setFormData] = useState({
    title: '',
    date: selectedDate,
    time: selectedTimeSlot || '09:00',
    duration: 60,
    customDuration: '',
    description: '',
    color: '#3498db',
    isRepeating: false,
    repeatType: 'none' as 'daily' | 'weekly' | 'monthly',
    repeatDays: '',
    repeatStartDate: selectedDate,
    repeatEndDate: ''
  });
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Update form data when event prop changes
  useEffect(() => {
    if (event) {
      console.log('EventForm: Updating form data for event:', event);
      setFormData({
        title: event.title || '',
        date: event.event_date || selectedDate,
        time: event.event_time ? event.event_time.substring(0, 5) : (selectedTimeSlot || '09:00'),
        duration: event.duration_minutes || 60,
        customDuration: event.duration_minutes && ![15, 30, 60, 90, 120, 180, 240].includes(event.duration_minutes) ? event.duration_minutes.toString() : '',
        description: event.description || '',
        color: event.color || '#3498db',
        isRepeating: event.is_repeating || false,
        repeatType: (event.repeat_type === 'none' ? 'daily' : event.repeat_type) || 'daily',
        repeatDays: event.repeat_days || '',
        repeatStartDate: event.repeat_start_date || event.event_date || selectedDate,
        repeatEndDate: event.repeat_end_date || ''
      });
    } else {
      // Reset to default values for new event
      console.log('EventForm: Resetting form data for new event');
      setFormData({
        title: '',
        date: selectedDate,
        time: selectedTimeSlot || '09:00',
        duration: 60,
        customDuration: '',
        description: '',
        color: '#3498db',
        isRepeating: false,
        repeatType: 'daily',
        repeatDays: '',
        repeatStartDate: selectedDate,
        repeatEndDate: ''
      });
    }
  }, [event, selectedDate, selectedTimeSlot]);

  const colors = [
    '#3498db', '#e74c3c', '#2ecc71', '#f39c12', 
    '#9b59b6', '#1abc9c', '#34495e', '#e67e22'
  ];

  // Validation functions
  const validateForm = () => {
    const errors: {[key: string]: string} = {};
    
    if (!formData.title.trim()) {
      errors.title = 'Event title is required';
    }
    
    if (!formData.date) {
      errors.date = 'Date is required';
    }
    
    if (!formData.time) {
      errors.time = 'Time is required';
    }
    
    const duration = formData.customDuration ? parseInt(formData.customDuration) : formData.duration;
    if (!duration || duration < 5 || duration > 720) {
      errors.duration = 'Duration must be between 5 and 720 minutes';
    }
    
    if (formData.isRepeating) {
      if (!formData.repeatStartDate) {
        errors.repeatStartDate = 'Start date is required for repeating events';
      }
      
      if (formData.repeatType === 'weekly' && !formData.repeatDays.trim()) {
        errors.repeatDays = 'Days of week are required for weekly repeating events';
      }
      
      if (formData.repeatEndDate && formData.repeatStartDate && formData.repeatEndDate < formData.repeatStartDate) {
        errors.repeatEndDate = 'End date must be after start date';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const clearErrors = () => {
    setFormErrors({});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearErrors();
    
    if (!validateForm() || !user) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const finalDuration = formData.customDuration ? parseInt(formData.customDuration) : formData.duration;
      const userId = typeof user.id === 'string' ? parseInt(user.id) : user.id;

      // Debug: Log the form data before sending
      console.log('Form submission - Time value:', formData.time);
      console.log('Form submission - Date value:', formData.date);
      console.log('Form submission - Repeat start date:', formData.repeatStartDate);

      const eventData: CreateScheduleEventData = {
        user_id: userId,
        title: formData.title.trim(),
        description: formData.description.trim() || undefined,
        event_date: formData.isRepeating ? undefined : formData.date,
        event_time: formData.time,
        duration_minutes: finalDuration,
        color: formData.color,
        is_repeating: formData.isRepeating,
        repeat_type: formData.isRepeating ? formData.repeatType as 'daily' | 'weekly' | 'monthly' : 'none',
        repeat_days: formData.isRepeating && formData.repeatDays ? formData.repeatDays : undefined,
        repeat_start_date: formData.isRepeating ? formData.repeatStartDate : undefined,
        repeat_end_date: formData.isRepeating && formData.repeatEndDate ? formData.repeatEndDate : undefined
      };

      console.log('Sending event data to API:', eventData);
      await onSave(eventData);
    } catch (error) {
      console.error('Error submitting form:', error);
      setFormErrors({ submit: 'Failed to save event. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content event-form">
        <div className="modal-header">
          <h3>{event ? 'Edit Event' : 'New Event'}</h3>
          <button className="close-btn" onClick={onCancel}>×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          {formErrors.submit && (
            <div className="error-message general-error">
              {formErrors.submit}
            </div>
          )}
          <div className="form-group">
            <label htmlFor="title">Event Title</label>
            <input
              type="text"
              id="title"
              value={formData.title}
              onChange={(e) => {
                setFormData({ ...formData, title: e.target.value });
                if (formErrors.title) clearErrors();
              }}
              placeholder="Enter event title"
              required
              className={formErrors.title ? 'error' : ''}
            />
            {formErrors.title && <div className="error-message">{formErrors.title}</div>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="date">Date</label>
              <input
                type="date"
                id="date"
                value={formData.date}
                onChange={(e) => {
                  setFormData({ ...formData, date: e.target.value });
                  if (formErrors.date) clearErrors();
                }}
                required
                className={formErrors.date ? 'error' : ''}
              />
              {formErrors.date && <div className="error-message">{formErrors.date}</div>}
            </div>
            
            <div className="form-group">
              <label htmlFor="time">Time</label>
              <input
                type="time"
                id="time"
                value={formData.time}
                onChange={(e) => {
                  setFormData({ ...formData, time: e.target.value });
                  if (formErrors.time) clearErrors();
                }}
                required
                className={formErrors.time ? 'error' : ''}
              />
              {formErrors.time && <div className="error-message">{formErrors.time}</div>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="duration">Duration (minutes)</label>
            <div className="duration-inputs">
              <select
                id="duration"
                value={formData.customDuration ? 'custom' : formData.duration}
                onChange={(e) => {
                  if (e.target.value === 'custom') {
                    setFormData({ ...formData, customDuration: formData.duration.toString() });
                  } else {
                    setFormData({ ...formData, duration: parseInt(e.target.value), customDuration: '' });
                  }
                  if (formErrors.duration) clearErrors();
                }}
                className={formErrors.duration ? 'error' : ''}
              >
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={60}>1 hour</option>
                <option value={90}>1.5 hours</option>
                <option value={120}>2 hours</option>
                <option value={180}>3 hours</option>
                <option value={240}>4 hours</option>
                <option value="custom">Custom duration</option>
              </select>
              {(formData.customDuration || formData.customDuration === '0') && (
                <input
                  type="number"
                  min="5"
                  max="720"
                  step="5"
                  value={formData.customDuration}
                  onChange={(e) => {
                    setFormData({ ...formData, customDuration: e.target.value });
                    if (formErrors.duration) clearErrors();
                  }}
                  placeholder="Custom minutes"
                  className={`custom-duration-input ${formErrors.duration ? 'error' : ''}`}
                />
              )}
            </div>
            {formErrors.duration && <div className="error-message">{formErrors.duration}</div>}
          </div>

          <div className="form-group">
            <label>Color</label>
            <div className="color-picker">
              {colors.map(color => (
                <button
                  key={color}
                  type="button"
                  className={`color-option ${formData.color === color ? 'selected' : ''}`}
                  style={{ backgroundColor: color }}
                  onClick={() => setFormData({ ...formData, color })}
                />
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.isRepeating}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  isRepeating: e.target.checked,
                  repeatType: e.target.checked ? 'daily' : 'daily'
                })}
              />
              Make this a repeating event
            </label>
          </div>

          {formData.isRepeating && (
            <>
              <div className="form-group">
                <label htmlFor="repeatType">Repeat Pattern</label>
                <select
                  id="repeatType"
                  value={formData.repeatType}
                  onChange={(e) => setFormData({ ...formData, repeatType: e.target.value as 'daily' | 'weekly' | 'monthly' })}
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>

              {formData.repeatType === 'weekly' && (
                <div className="form-group">
                  <label htmlFor="repeatDays">Days of Week (1=Mon, 7=Sun)</label>
                  <input
                    type="text"
                    id="repeatDays"
                    value={formData.repeatDays}
                    onChange={(e) => {
                      setFormData({ ...formData, repeatDays: e.target.value });
                      if (formErrors.repeatDays) clearErrors();
                    }}
                    placeholder="e.g., 1,3,5 for Mon,Wed,Fri"
                    className={formErrors.repeatDays ? 'error' : ''}
                  />
                  {formErrors.repeatDays && <div className="error-message">{formErrors.repeatDays}</div>}
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="repeatStartDate">Start Date</label>
                  <input
                    type="date"
                    id="repeatStartDate"
                    value={formData.repeatStartDate}
                    onChange={(e) => {
                      setFormData({ ...formData, repeatStartDate: e.target.value });
                      if (formErrors.repeatStartDate) clearErrors();
                    }}
                    required
                    className={formErrors.repeatStartDate ? 'error' : ''}
                  />
                  {formErrors.repeatStartDate && <div className="error-message">{formErrors.repeatStartDate}</div>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="repeatEndDate">End Date (optional)</label>
                  <input
                    type="date"
                    id="repeatEndDate"
                    value={formData.repeatEndDate}
                    onChange={(e) => {
                      setFormData({ ...formData, repeatEndDate: e.target.value });
                      if (formErrors.repeatEndDate) clearErrors();
                    }}
                    className={formErrors.repeatEndDate ? 'error' : ''}
                  />
                  {formErrors.repeatEndDate && <div className="error-message">{formErrors.repeatEndDate}</div>}
                </div>
              </div>
            </>
          )}

          <div className="form-group">
            <label htmlFor="description">Description (optional)</label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Add event description..."
              rows={3}
            />
          </div>

          <div className="form-actions">
            {onDelete && (
              <button
                type="button"
                className="delete-btn"
                onClick={() => {
                  if (window.confirm('Are you sure you want to delete this event?')) {
                    onDelete();
                  }
                }}
              >
                Delete
              </button>
            )}
            <div className="action-buttons">
              <button type="button" className="cancel-btn" onClick={onCancel}>
                Cancel
              </button>
              <button type="submit" className="save-btn" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : (event ? 'Update' : 'Create')} Event
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

interface DeleteEventModalProps {
  event: ScheduleEvent;
  currentDate: Date;
  onDeleteInstance: () => void;
  onDeleteSeries: () => void;
  onCancel: () => void;
}

const DeleteEventModal: React.FC<DeleteEventModalProps> = ({ 
  event, 
  currentDate, 
  onDeleteInstance, 
  onDeleteSeries, 
  onCancel 
}) => {
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content delete-event-modal">
        <div className="modal-header">
          <h3>Delete Event</h3>
          <button className="close-btn" onClick={onCancel}>×</button>
        </div>
        
        <div className="delete-modal-body">
          <div className="event-info">
            <div className="event-info-title">{event.title}</div>
            {event.is_repeating && (
              <div className="event-info-meta">
                <span className="repeating-badge">🔄 Repeating Event</span>
              </div>
            )}
          </div>

          {event.is_repeating ? (
            <>
              <p className="delete-modal-description">
                This is a repeating event. Choose how you want to delete it:
              </p>
              <div className="delete-options">
                <button 
                  className="delete-option-btn delete-instance"
                  onClick={onDeleteInstance}
                >
                  <div className="option-icon">📅</div>
                  <div className="option-content">
                    <div className="option-title">Delete This Instance Only</div>
                    <div className="option-description">
                      Remove only the occurrence on {formatDate(currentDate)}
                    </div>
                  </div>
                </button>

                <button 
                  className="delete-option-btn delete-series"
                  onClick={onDeleteSeries}
                >
                  <div className="option-icon">🗑️</div>
                  <div className="option-content">
                    <div className="option-title">Delete Entire Series</div>
                    <div className="option-description">
                      Remove all occurrences of this repeating event
                    </div>
                  </div>
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="delete-modal-description">
                Are you sure you want to delete this event?
              </p>
              <div className="delete-options">
                <button 
                  className="delete-option-btn delete-single"
                  onClick={onDeleteSeries}
                >
                  <div className="option-icon">🗑️</div>
                  <div className="option-content">
                    <div className="option-title">Delete Event</div>
                    <div className="option-description">
                      This action cannot be undone
                    </div>
                  </div>
                </button>
              </div>
            </>
          )}

          <div className="delete-modal-actions">
            <button className="cancel-btn" onClick={onCancel}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Schedule;
